import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ChevronLeft, ChevronRight, CalendarDays, Clock, FileText } from 'lucide-react';

const CHANNEL_COLORS = {
  instagram: '#E1306C', youtube: '#FF0033', twitter: '#A8B3BD', facebook: '#1877F2',
  whatsapp: '#22C55E', telegram: '#2AABEE', sms: '#84CC16', email: '#F59E0B'
};

const DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

export default function CalendarPage() {
  const { t } = useTranslation();
  const { api } = useAuth();
  const [currentDate, setCurrentDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);

  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  useEffect(() => {
    setLoading(true);
    api.get('/api/scheduler/calendar', { params: { month: month + 1, year } })
      .then(res => setPosts(res.data))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [api, month, year]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstDay = (new Date(year, month, 1).getDay() + 6) % 7; // Monday-start

  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const goToday = () => setCurrentDate(new Date());

  const getPostsForDay = (day) => {
    return posts.filter(p => {
      const d = new Date(p.scheduled_at || p.created_at);
      return d.getDate() === day && d.getMonth() === month && d.getFullYear() === year;
    });
  };

  const today = new Date();
  const isToday = (day) => day === today.getDate() && month === today.getMonth() && year === today.getFullYear();

  const calendarDays = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
          {t('calendar.title')}
        </h1>
      </div>

      <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="icon" onClick={prevMonth} className="hover:bg-white/5">
                <ChevronLeft size={18} />
              </Button>
              <h2 className="text-lg font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                {MONTHS[month]} {year}
              </h2>
              <Button variant="ghost" size="icon" onClick={nextMonth} className="hover:bg-white/5">
                <ChevronRight size={18} />
              </Button>
            </div>
            <Button variant="outline" size="sm" onClick={goToday} className="border-white/10" data-testid="calendar-today">
              {t('calendar.today')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Day headers */}
          <div className="grid grid-cols-7 gap-1 mb-2">
            {DAYS.map(d => (
              <div key={d} className="text-center text-xs font-medium text-[hsl(var(--muted-foreground))] py-2">{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, i) => {
              if (!day) return <div key={`empty-${i}`} className="min-h-[80px]" />;
              const dayPosts = getPostsForDay(day);
              return (
                <button
                  key={day}
                  onClick={() => setSelectedDay(day === selectedDay ? null : day)}
                  data-testid={`calendar-day-${day}`}
                  className={`min-h-[80px] p-1.5 rounded-lg border text-left transition-colors duration-200 ${
                    isToday(day)
                      ? 'border-[hsl(var(--primary))]/40 bg-[hsl(var(--primary))]/5'
                      : selectedDay === day
                        ? 'border-[hsl(var(--accent))]/40 bg-[hsl(var(--accent))]/5'
                        : 'border-white/5 hover:border-white/15 hover:bg-white/5'
                  }`}
                >
                  <span className={`text-xs font-medium ${
                    isToday(day) ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--muted-foreground))]'
                  }`}>{day}</span>
                  <div className="mt-1 space-y-0.5">
                    {dayPosts.slice(0, 3).map((p, pi) => (
                      <div
                        key={pi}
                        className="text-[10px] px-1 py-0.5 rounded truncate border-l-2"
                        style={{ borderLeftColor: CHANNEL_COLORS[p.channels?.[0]] || '#666', backgroundColor: 'rgba(255,255,255,0.05)' }}
                      >
                        {p.content?.substring(0, 20)}
                      </div>
                    ))}
                    {dayPosts.length > 3 && (
                      <span className="text-[10px] text-[hsl(var(--muted-foreground))]">+{dayPosts.length - 3}</span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Selected Day Detail */}
      {selectedDay && (
        <Card className="rounded-xl border border-white/10 bg-[hsl(var(--card))]/80">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">
              {selectedDay} {MONTHS[month]} {year}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {getPostsForDay(selectedDay).length === 0 ? (
              <p className="text-sm text-[hsl(var(--muted-foreground))] text-center py-4">{t('calendar.noEvents')}</p>
            ) : (
              <div className="space-y-3">
                {getPostsForDay(selectedDay).map((post, i) => (
                  <div key={i} className="p-3 rounded-lg border border-white/10 bg-white/5">
                    <div className="flex items-start justify-between">
                      <p className="text-sm">{post.content?.substring(0, 100)}</p>
                      <Badge variant="outline" className={`ml-2 text-[10px] ${
                        post.status === 'published' ? 'border-emerald-500/30 text-emerald-300' :
                        post.status === 'scheduled' ? 'border-sky-500/30 text-sky-300' :
                        'border-white/20'
                      }`}>
                        {post.status}
                      </Badge>
                    </div>
                    <div className="flex items-center gap-2 mt-2">
                      <Clock size={12} className="text-[hsl(var(--muted-foreground))]" />
                      <span className="text-xs text-[hsl(var(--muted-foreground))]">
                        {new Date(post.scheduled_at || post.created_at).toLocaleTimeString()}
                      </span>
                      {post.channels?.map(ch => (
                        <div key={ch} className="w-3 h-3 rounded-full" style={{ backgroundColor: CHANNEL_COLORS[ch] }} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
